import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Wallet, ArrowLeft, Download, Plus, Loader2, Trash2, School as SchoolIcon, ArrowDownLeft } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";

const rs = (paise) => `₹${(paise / 100).toLocaleString("en-IN")}`;
const token = () => localStorage.getItem("munners_token") || "";
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function SchoolLedger() {
  const { accountId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showReceived, setShowReceived] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/accountant/school-accounts/${accountId}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [accountId]);

  const downloadStatement = () => window.open(`${BACKEND_URL}/api/accountant/school-accounts/${accountId}/statement.pdf?t=${token()}`, "_blank");

  const delPayout = async (p) => {
    if (!window.confirm(`Reverse payout of ${rs(p.amount_paise)} for ${p.students_covered} student(s)? This will re-open the balance.`)) return;
    try {
      await api.delete(`/accountant/school-accounts/${accountId}/payouts/${p.payout_id}`);
      toast.success("Payout reversed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (loading || !data) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const { account, ledger, students, payouts, received = [] } = data;

  const delReceived = async (x) => {
    if (!window.confirm(`Delete received payment of ${rs(x.amount_paise)} (${x.remarks})? The balance will go down by this amount.`)) return;
    try {
      await api.delete(`/accountant/school-accounts/${accountId}/received/${x.received_id}`);
      toast.success("Received payment removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="school-ledger-page">
      <div className="max-w-6xl mx-auto p-6 lg:p-10">
        <button onClick={() => window.close()} className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 mb-4" data-testid="school-back">
          <ArrowLeft className="w-4 h-4" /> Close tab
        </button>

        <div className="dashboard-card p-6 mb-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-emerald-500 text-white flex items-center justify-center"><SchoolIcon className="w-7 h-7" /></div>
            <div>
              <h1 className="font-display font-black text-2xl">{account.school_name}</h1>
              <div className="text-sm text-slate-500">{account.event_name} · Kaimera fee/student: <span className="font-mono">{rs(account.kaimera_fee_per_delegate_paise)}</span></div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setShowAdd(true)} className="btn-primary text-sm" data-testid="school-add-payout"><Plus className="w-4 h-4" /> Add Payment</button>
            <button onClick={() => setShowReceived(true)} className="btn-ghost text-sm text-sky-700" data-testid="school-add-received"><ArrowDownLeft className="w-4 h-4" /> Record received payment</button>
            <button onClick={downloadStatement} className="btn-ghost text-sm" data-testid="school-download-statement"><Download className="w-4 h-4" /> Statement PDF</button>
          </div>
        </div>

        {/* Ledger summary */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
          <Stat label="Collected" value={rs(ledger.total_collected_paise)} color="text-slate-900" testid="ledger-collected" />
          <Stat label="Kaimera share" value={rs(ledger.kaimera_retained_paise)} color="text-emerald-600" testid="ledger-kaimera" />
          <Stat label="Received from school" value={rs(ledger.received_from_school_paise || 0)} color="text-sky-700" testid="ledger-received" />
          <Stat label="Owed to school" value={rs(ledger.school_owed_paise)} color="text-slate-900" testid="ledger-owed" />
          <Stat label={ledger.tds_paise ? `Paid out (incl. ${rs(ledger.tds_paise)} TDS)` : "Paid out"} value={rs(ledger.school_paid_paise)} color="text-slate-700" testid="ledger-paid" />
          <Stat label="Balance due" value={rs(ledger.school_balance_paise)} color={ledger.school_balance_paise > 0 ? "text-rose-600" : "text-emerald-600"} big testid="ledger-balance" />
        </div>
        <div className="dashboard-card p-4 mb-5 flex items-center gap-3 bg-orange-50/40 border-l-4 border-orange-400">
          <Wallet className="w-5 h-5 text-orange-600" />
          <div className="text-sm"><span className="font-bold">{ledger.students_covered_by_payouts} of {ledger.students_paid_count}</span> student{ledger.students_paid_count === 1 ? "" : "s"} covered by payouts so far. <span className="text-slate-500">Uncovered: {ledger.students_uncovered}.</span></div>
        </div>

        {/* Students paid */}
        <div className="dashboard-card p-5 mb-5">
          <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold text-lg">Students who paid ({students.length})</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase text-slate-500 border-b border-slate-100">
                <th className="py-2 pr-2">Receipt</th><th className="py-2 pr-2">Student</th><th className="py-2 pr-2">Contact</th>
                <th className="py-2 pr-2 text-right">Amount</th><th className="py-2 pr-2 text-right">Kaimera share</th><th className="py-2 pr-2 text-right">School share</th><th className="py-2 pr-2">Issued</th>
              </tr></thead>
              <tbody>
                {students.length === 0 ? <tr><td colSpan={7} className="py-8 text-center text-slate-400">No receipts yet.</td></tr>
                  : students.map((r) => {
                    // Use the per-receipt Kaimera share coming from the
                    // backend (derived from that receipt's journal legs,
                    // which honour per-form `kaimera_share_paise`). Old
                    // rows without journal legs still fall back to the
                    // account default server-side.
                    const km = typeof r.kaimera_share_paise === "number"
                      ? r.kaimera_share_paise
                      : account.kaimera_fee_per_delegate_paise;
                    const school = typeof r.school_share_paise === "number"
                      ? r.school_share_paise
                      : Math.max(r.amount_paise - km, 0);
                    return (
                      <tr key={r.receipt_id} className="border-b border-slate-50" data-testid={`student-row-${r.receipt_id}`}>
                        <td className="py-2 pr-2 font-mono">{r.receipt_number}</td>
                        <td className="py-2 pr-2 font-bold">{r.payer_name}</td>
                        <td className="py-2 pr-2 text-xs text-slate-500">{r.payer_email || r.payer_phone || "—"}</td>
                        <td className="py-2 pr-2 text-right font-bold">{rs(r.amount_paise)}</td>
                        <td className="py-2 pr-2 text-right text-emerald-700 font-mono">{rs(km)}</td>
                        <td className="py-2 pr-2 text-right font-mono">{rs(school)}</td>
                        <td className="py-2 pr-2 text-xs text-slate-500">{(r.issued_at || "").slice(0, 10)}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Payouts */}
        <div className="dashboard-card p-5">
          <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold text-lg">Payouts to school ({payouts.length})</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase text-slate-500 border-b border-slate-100">
                <th className="py-2 pr-2">Date</th><th className="py-2 pr-2">Mode</th><th className="py-2 pr-2">Reference</th>
                <th className="py-2 pr-2 text-right">Students</th><th className="py-2 pr-2 text-right">Amount</th><th className="py-2 pr-2">Note</th><th className="py-2 pr-2 text-right"></th>
              </tr></thead>
              <tbody>
                {payouts.length === 0 ? <tr><td colSpan={7} className="py-8 text-center text-slate-400">No payouts yet — click <b>Add Payment</b> after transferring money from your bank.</td></tr>
                  : payouts.map((p) => (
                    <tr key={p.payout_id} className="border-b border-slate-50" data-testid={`payout-row-${p.payout_id}`}>
                      <td className="py-2 pr-2 text-xs">{(p.paid_at || "").slice(0, 10)}</td>
                      <td className="py-2 pr-2 text-xs uppercase font-bold">{p.payment_mode === "tds" ? <span className="text-amber-700">TDS</span> : p.payment_mode}</td>
                      <td className="py-2 pr-2 font-mono text-xs">{p.reference || "—"}</td>
                      <td className="py-2 pr-2 text-right font-bold">{p.payment_mode === "tds" ? "—" : p.students_covered}</td>
                      <td className="py-2 pr-2 text-right font-bold">{rs(p.amount_paise)}</td>
                      <td className="py-2 pr-2 text-xs text-slate-500">{p.note || "—"}</td>
                      <td className="py-2 pr-2 text-right"><button onClick={() => delPayout(p)} className="text-rose-500 hover:bg-rose-50 p-1.5 rounded" title="Reverse payout"><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Payments received FROM the school */}
        <div className="dashboard-card p-5 mt-5" data-testid="received-section">
          <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold text-lg">Payments received from school ({received.length})</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase text-slate-500 border-b border-slate-100">
                <th className="py-2 pr-2">Date</th><th className="py-2 pr-2">Mode</th><th className="py-2 pr-2">Reference</th>
                <th className="py-2 pr-2">Remarks</th><th className="py-2 pr-2 text-right">Amount</th><th className="py-2 pr-2 text-right"></th>
              </tr></thead>
              <tbody>
                {received.length === 0 ? <tr><td colSpan={6} className="py-8 text-center text-slate-400">Nothing received yet — use <b>Record received payment</b> when the school sends you money.</td></tr>
                  : received.map((x) => (
                    <tr key={x.received_id} className="border-b border-slate-50" data-testid={`received-row-${x.received_id}`}>
                      <td className="py-2 pr-2 text-xs">{(x.received_at || "").slice(0, 10)}</td>
                      <td className="py-2 pr-2 text-xs uppercase font-bold">{x.payment_mode}</td>
                      <td className="py-2 pr-2 font-mono text-xs">{x.reference || "—"}</td>
                      <td className="py-2 pr-2">{x.remarks}</td>
                      <td className="py-2 pr-2 text-right font-bold text-sky-700">{rs(x.amount_paise)}</td>
                      <td className="py-2 pr-2 text-right"><button onClick={() => delReceived(x)} className="text-rose-500 hover:bg-rose-50 p-1.5 rounded" title="Delete entry" data-testid={`received-del-${x.received_id}`}><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {showAdd && <AddPayoutModal accountId={accountId} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
        {showReceived && <AddReceivedModal accountId={accountId} onClose={() => setShowReceived(false)} onSaved={() => { setShowReceived(false); load(); }} />}
      </div>
    </div>
  );
}

function Stat({ label, value, color, big, testid }) {
  return (
    <div className="dashboard-card p-4" data-testid={testid}>
      <div className="text-[10px] uppercase font-bold text-slate-500">{label}</div>
      <div className={`font-display font-black ${big ? "text-2xl" : "text-xl"} ${color}`}>{value}</div>
    </div>
  );
}

function AddPayoutModal({ accountId, onClose, onSaved }) {
  const today = localToday();
  const [form, setForm] = useState({ amount: "", students: "", mode: "bank_transfer", reference: "", paid_at: today, note: "" });
  const [saving, setSaving] = useState(false);
  const isTds = form.mode === "tds";

  const save = async (e) => {
    e.preventDefault();
    if (!form.amount || (!isTds && !form.students)) { toast.error(isTds ? "Amount is required" : "Amount and students-covered are required"); return; }
    setSaving(true);
    try {
      await api.post(`/accountant/school-accounts/${accountId}/payouts`, {
        amount_paise: Math.round(parseFloat(form.amount) * 100),
        students_covered: isTds ? 0 : parseInt(form.students, 10),
        payment_mode: form.mode,
        reference: form.reference.trim(),
        paid_at: `${form.paid_at}T${new Date().toISOString().slice(11, 19)}`,
        note: form.note.trim(),
      });
      toast.success(isTds ? "TDS recorded — balance updated, students covered unchanged" : "Payout recorded — balance updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4" onClick={(e) => e.stopPropagation()} data-testid="payout-modal">
        <div className="flex items-center justify-between"><h3 className="font-display font-black text-xl">Add payment to school</h3><button type="button" onClick={onClose} className="text-slate-500 text-2xl leading-none">×</button></div>
        <p className="text-sm text-slate-500">Record the transfer you just made from your bank. The balance will decrease and the principal will see the new student-count covered.</p>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-slate-500">Amount (₹) *</label><input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="input-pill mt-1" data-testid="payout-amount" /></div>
          <div><label className="text-xs text-slate-500">Students covered {isTds ? "(not needed for TDS)" : "*"}</label><input required={!isTds} disabled={isTds} type="number" min="1" step="1" value={isTds ? "" : form.students} placeholder={isTds ? "Unchanged" : ""} onChange={(e) => setForm({ ...form, students: e.target.value })} className="input-pill mt-1 disabled:opacity-50" data-testid="payout-students" /></div>
          <div><label className="text-xs text-slate-500">Payment mode</label>
            <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="input-pill mt-1" data-testid="payout-mode">
              <option value="bank_transfer">Bank transfer</option>
              <option value="cash">Cash</option>
              <option value="cheque">Cheque</option>
              <option value="upi">UPI</option>
              <option value="neft">NEFT</option>
              <option value="rtgs">RTGS</option>
              <option value="imps">IMPS</option>
              <option value="tds">TDS (tax deducted)</option>
            </select>
          </div>
          <div><label className="text-xs text-slate-500">Date</label><input type="date" value={form.paid_at} onChange={(e) => setForm({ ...form, paid_at: e.target.value })} className="input-pill mt-1" data-testid="payout-date" /></div>
          <div className="col-span-2"><label className="text-xs text-slate-500">Reference # (UTR / cheque / UPI ID)</label><input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="input-pill mt-1" data-testid="payout-ref" /></div>
          <div className="col-span-2"><label className="text-xs text-slate-500">Note (optional)</label><textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="input-pill mt-1" rows={2} data-testid="payout-note" /></div>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="payout-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {saving ? "Saving…" : "Record payment"}</button>
        </div>
      </form>
    </div>
  );
}

// Money the SCHOOL paid to you — adds to what you owe the school and shows on the statement.
function AddReceivedModal({ accountId, onClose, onSaved }) {
  const [form, setForm] = useState({ amount: "", remarks: "", mode: "bank_transfer", reference: "", date: localToday() });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.remarks.trim()) { toast.error("Amount and remarks are required"); return; }
    setSaving(true);
    try {
      await api.post(`/accountant/school-accounts/${accountId}/received`, {
        amount_paise: Math.round(parseFloat(form.amount) * 100),
        remarks: form.remarks.trim(),
        payment_mode: form.mode,
        reference: form.reference.trim(),
        received_date: form.date,
      });
      toast.success("Received payment recorded — balance updated");
      onSaved();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4" onClick={(e) => e.stopPropagation()} data-testid="received-modal">
        <div className="flex items-center justify-between"><h3 className="font-display font-black text-xl">Record payment received from school</h3><button type="button" onClick={onClose} className="text-slate-500 text-2xl leading-none" data-testid="received-close">×</button></div>
        <p className="text-sm text-slate-500">Use this when the school sends money to you (e.g. refund of an excess payout). "Owed to school" stays the same — only the balance due changes. It also appears on the statement.</p>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-slate-500">Amount (₹) *</label><input required type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="input-pill mt-1" data-testid="received-amount" /></div>
          <div><label className="text-xs text-slate-500">Date</label><input type="date" max={localToday()} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-pill mt-1" data-testid="received-date" /></div>
          <div className="col-span-2"><label className="text-xs text-slate-500">Remarks * (what is this payment for?)</label><input required value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="e.g. Advance for venue, refund of overpayment" className="input-pill mt-1" data-testid="received-remarks" /></div>
          <div><label className="text-xs text-slate-500">Payment mode</label>
            <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="input-pill mt-1" data-testid="received-mode">
              <option value="bank_transfer">Bank transfer</option>
              <option value="upi">UPI</option>
              <option value="cash">Cash</option>
              <option value="cheque">Cheque</option>
              <option value="neft">NEFT</option>
              <option value="rtgs">RTGS</option>
              <option value="imps">IMPS</option>
            </select>
          </div>
          <div><label className="text-xs text-slate-500">Reference #</label><input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="input-pill mt-1" data-testid="received-ref" /></div>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="received-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {saving ? "Saving…" : "Record received payment"}</button>
        </div>
      </form>
    </div>
  );
}
